import React, { useState, useEffect } from 'react';
import { runMasterAnalysis } from '../../engines/masterBrainV2';
import { getAgentRoster } from '../../engines/agentFactory';
import { getLifecycleSummary, STATUS_META, DNA_META } from '../../engines/strategyLifecycle';
import { THEME } from '../../styles/theme';

const S = {
  wrap:      { padding: '20px', maxWidth: '1100px', margin: '0 auto' },
  header:    { marginBottom: '24px' },
  title:     { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px' },
  sub:       { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },

  // Summary bar
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' },
  sumCard:   (col='#fff') => ({ background: 'rgba(255,255,255,0.03)', border: `1px solid ${col}22`, borderRadius: '10px', padding: '14px' }),
  sumLbl:    { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' },
  sumVal:    (col) => ({ fontSize: '22px', fontWeight: '800', color: col || '#fff', fontFamily: THEME.fonts.hud }),

  // Quick questions
  qRow:      { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  qBtn:      { padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(0,212,255,0.2)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s' },

  // Input row
  inputRow:  { display: 'flex', gap: '10px', marginBottom: '24px' },
  input:     { flex: 1, padding: '13px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none' },
  runBtn:    (running) => ({ padding: '13px 28px', borderRadius: '8px', border: 'none', background: running ? '#6b7280' : THEME.colors.blue, color: '#000', cursor: running ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '14px', fontFamily: THEME.fonts.hud, whiteSpace: 'nowrap' }),

  // Agent roster (before run)
  rosterGrid:{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px', marginBottom: '24px' },
  rosterCard:{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' },
  rIcon:     (col) => ({ width: '36px', height: '36px', borderRadius: '8px', background: col+'22', border: `1px solid ${col}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }),
  rName:     { fontSize: '13px', fontWeight: '600', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rMeta:     { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  rBadge:    (col) => ({ display: 'inline-block', padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', background: col+'22', color: col, border: `1px solid ${col}33`, marginLeft: '4px' }),

  // Live agent cards (during/after run)
  liveGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: '24px' },
  liveCard:  (status, verdict) => ({
    background:   status === 'running' ? 'rgba(0,212,255,0.05)' : status === 'error' ? 'rgba(239,68,68,0.05)' : 'rgba(255,255,255,0.03)',
    border:       `1px solid ${status === 'running' ? 'rgba(0,212,255,0.3)' : status === 'error' ? 'rgba(239,68,68,0.2)' : verdictColor(verdict)+'33'}`,
    borderRadius: '10px',
    padding:      '14px',
    transition:   'all 0.3s',
  }),
  lName:     { fontSize: '13px', fontWeight: '700', color: '#e2e8f0', marginBottom: '4px' },
  lMeta:     { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '8px' },
  lVerdict:  (verdict) => ({ fontSize: '16px', fontWeight: '800', color: verdictColor(verdict), fontFamily: THEME.fonts.hud }),
  lConf:     { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginLeft: '8px' },
  lReason:   { fontSize: '12px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.5' },
  lWeight:   { fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '4px' },

  // Consensus bar
  consBox:   { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  consTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '14px', fontFamily: THEME.fonts.hud },
  consBar:   { display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', marginBottom: '8px' },
  consSeg:   (pct, col) => ({ width: pct + '%', background: col, transition: 'width 0.5s' }),
  consLgd:   { display: 'flex', gap: '16px', marginBottom: '14px' },
  consLbl:   (col) => ({ fontSize: '12px', color: col, fontWeight: '700' }),
  consNums:  { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },

  // Master verdict card
  masterCard:(verdict) => ({
    background:   verdictColor(verdict) + '08',
    border:       `2px solid ${verdictColor(verdict)}44`,
    borderRadius: '14px',
    padding:      '24px',
    marginBottom: '20px',
  }),
  mBadge:    { fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px', fontFamily: THEME.fonts.hud },
  mVerdict:  (v) => ({ fontSize: '42px', fontWeight: '900', color: verdictColor(v), fontFamily: THEME.fonts.hud, letterSpacing: '4px', marginBottom: '8px' }),
  mConf:     { fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' },
  mReason:   { fontSize: '15px', color: '#e2e8f0', lineHeight: '1.7', marginBottom: '16px' },
  mAction:   { background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', padding: '14px', fontSize: '14px', color: THEME.colors.blue, fontWeight: '600' },
  mGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginTop: '16px' },
  mStat:     { background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px 12px' },
  mStatLbl:  { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' },
  mStatVal:  (c='#fff') => ({ fontSize: '16px', fontWeight: '700', color: c, fontFamily: THEME.fonts.hud }),

  empty:     { textAlign: 'center', padding: '60px 20px', color: THEME.colors.textMuted },
  spinner:   { display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', border: '2px solid rgba(0,212,255,0.3)', borderTopColor: THEME.colors.blue, animation: 'spin 0.7s linear infinite', marginRight: '8px', verticalAlign: 'middle' },
};

function verdictColor(v) {
  if (v === 'BUY')  return '#10b981';
  if (v === 'SELL') return '#ef4444';
  return '#f59e0b';
}

const QUICK_QUESTIONS = [
  'Should I trade right now?',
  'Is this a high-probability setup?',
  'What is the best entry level?',
  'Should I manage my open trade?',
];

export default function AgentNetwork() {
  const [question,    setQuestion]    = useState('');
  const [running,     setRunning]     = useState(false);
  const [liveSteps,   setLiveSteps]   = useState([]);
  const [result,      setResult]      = useState(null);
  const [error,       setError]       = useState('');
  const [summary,     setSummary]     = useState(null);
  const [roster,      setRoster]      = useState([]);

  useEffect(() => {
    setSummary(getLifecycleSummary());
    setRoster(getAgentRoster());
  }, []);

  const launch = async (q) => {
    const finalQ = q || question || 'Should I trade right now?';
    setRunning(true);
    setLiveSteps([]);
    setResult(null);
    setError('');

    try {
      const res = await runMasterAnalysis(finalQ, (step) => {
        setLiveSteps(prev => {
          const idx = prev.findIndex(s => s.name === step.name);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = step;
            return next;
          }
          return [...prev, step];
        });
      });

      if (!res.success) {
        setError(res.error || 'Analysis failed');
      } else {
        setResult(res);
        setSummary(getLifecycleSummary());
        setRoster(getAgentRoster());
      }
    } catch (e) {
      setError(e.message);
    }
    setRunning(false);
  };

  const { consensus, masterVerdict } = result || {};

  const totalW = consensus?.totalWeight || 1;
  const buyPct  = Math.round((consensus?.buyWeight  || 0) / totalW * 100);
  const sellPct = Math.round((consensus?.sellWeight || 0) / totalW * 100);
  const waitPct = Math.round((consensus?.waitWeight || 0) / totalW * 100);

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>AGENT NETWORK</div>
        <div style={S.sub}>
          Dynamic multi-agent system — each proven strategy spawns its own AI agent.
          Master Brain synthesizes ALL reports.
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={S.summaryRow}>
          {[
            { label: 'ACTIVE AGENTS',   value: summary.activeAgents, color: THEME.colors.blue },
            { label: 'TOTAL STRATEGIES',value: summary.total,         color: '#fff' },
            { label: 'PROVEN',          value: summary.byStatus?.PROVEN || 0,         color: '#3b82f6' },
            { label: 'ELITE',           value: (summary.byStatus?.ELITE || 0) + (summary.byStatus?.STRONG || 0), color: '#00d4ff' },
            { label: 'HALL OF FAME',    value: summary.byStatus?.HALL_OF_FAME || 0,   color: '#f59e0b' },
            { label: 'AVG WIN RATE',    value: summary.avgWinRate + '%',              color: summary.avgWinRate >= 60 ? '#10b981' : '#f59e0b' },
            { label: 'TESTING',         value: summary.byStatus?.TESTING || 0,        color: '#f59e0b' },
            { label: 'RETIRED',         value: summary.byStatus?.RETIRED || 0,        color: '#ef4444' },
          ].map(item => (
            <div key={item.label} style={S.sumCard(item.color)}>
              <div style={S.sumLbl}>{item.label}</div>
              <div style={S.sumVal(item.color)}>{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick questions */}
      <div style={S.qRow}>
        {QUICK_QUESTIONS.map(q => (
          <button key={q} style={S.qBtn}
            onClick={() => { setQuestion(q); launch(q); }}
            onMouseEnter={e => e.currentTarget.style.color = THEME.colors.blue}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >{q}</button>
        ))}
      </div>

      {/* Custom question input */}
      <div style={S.inputRow}>
        <input
          style={S.input}
          placeholder="Ask Master JARVIS anything about current market..."
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !running && launch()}
        />
        <button style={S.runBtn(running)} onClick={() => !running && launch()}>
          {running ? (
            <><span style={S.spinner}/>Running...</>
          ) : (
            `Run ${roster.length > 0 ? roster.length : 'All'} Agents`
          )}
        </button>
      </div>

      {/* No agents state */}
      {roster.length === 0 && !running && !result && (
        <div style={S.empty}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🤖</div>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px', color: '#e2e8f0' }}>
            No Active Agents Yet
          </div>
          <div style={{ fontSize: '14px', marginBottom: '16px' }}>
            Agents spawn automatically when strategies reach PROVEN status.
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)', maxWidth: '500px', margin: '0 auto', lineHeight: '1.7' }}>
            <b style={{ color: '#f59e0b' }}>How to activate agents:</b><br/>
            1. Log trades in Trade Journal → select the strategy used<br/>
            2. Win 6+ out of 10 trades with a strategy (≥55% win rate)<br/>
            3. JARVIS automatically promotes it to PROVEN → agent spawns<br/>
            4. More wins → ELITE → HALL OF FAME → higher weight in consensus
          </div>
        </div>
      )}

      {/* Agent Roster (pre-run) */}
      {roster.length > 0 && !running && !result && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>
            ACTIVE AGENTS ({roster.length}) — ready to analyze
          </div>
          <div style={S.rosterGrid}>
            {roster.map(a => {
              const dnaMeta = DNA_META[a.dna] || DNA_META.GENERAL;
              const stMeta  = STATUS_META[a.status] || STATUS_META.PROVEN;
              return (
                <div key={a.id} style={S.rosterCard}>
                  <div style={S.rIcon(dnaMeta.color)}>{dnaMeta.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.rName}>{a.name}</div>
                    <div style={S.rMeta}>
                      {dnaMeta.label}
                      <span style={S.rBadge(stMeta.color)}>{stMeta.icon} {stMeta.label}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                      Win Rate: <b style={{ color: a.winRate >= 65 ? '#10b981' : '#f59e0b' }}>{a.winRate}%</b>
                      &nbsp;·&nbsp;{a.totalTrades} trades
                      &nbsp;·&nbsp;Weight: <b style={{ color: THEME.colors.blue }}>{a.weight}x</b>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live agent cards during run */}
      {(running || result) && liveSteps.length > 0 && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>
            {running ? `AGENTS RUNNING (${liveSteps.filter(s => s.status === 'done').length}/${roster.length} done)...` : `ALL ${liveSteps.length} AGENTS COMPLETE`}
          </div>
          <div style={S.liveGrid}>
            {liveSteps.map((step, i) => {
              const dnaMeta = DNA_META[step.dna] || DNA_META.GENERAL;
              return (
                <div key={i} style={S.liveCard(step.status, step.verdict)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div>
                      <div style={S.lName}>
                        {step.status === 'running' && <span style={S.spinner}/>}
                        {dnaMeta.icon} {step.name}
                      </div>
                      <div style={S.lMeta}>
                        {dnaMeta.label}
                        {step.winRate ? ` · ${step.winRate}% WR` : ''}
                        {step.weight ? ` · ${step.weight}x weight` : ''}
                      </div>
                    </div>
                    {step.status === 'done' && step.verdict && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={S.lVerdict(step.verdict)}>{step.verdict}</div>
                        {step.confidence != null && <div style={S.lConf}>{step.confidence}% conf</div>}
                      </div>
                    )}
                    {step.status === 'error' && (
                      <div style={{ color: '#ef4444', fontSize: '12px' }}>Error</div>
                    )}
                    {step.status === 'running' && (
                      <div style={{ color: THEME.colors.blue, fontSize: '12px' }}>Analyzing...</div>
                    )}
                  </div>
                  {step.report?.reason && (
                    <div style={S.lReason}>{step.report.reason}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weighted Consensus Bar */}
      {consensus && (
        <div style={S.consBox}>
          <div style={S.consTitle}>
            WEIGHTED CONSENSUS — {consensus.agentCount} AGENTS · {consensus.agreeingCount} AGREE ({consensus.consensusStrength}%)
          </div>
          <div style={S.consBar}>
            <div style={S.consSeg(buyPct,  '#10b981')}/>
            <div style={S.consSeg(sellPct, '#ef4444')}/>
            <div style={S.consSeg(waitPct, '#f59e0b')}/>
          </div>
          <div style={S.consLgd}>
            <span style={S.consLbl('#10b981')}>BUY {buyPct}% ({consensus.buyWeight}w)</span>
            <span style={S.consLbl('#ef4444')}>SELL {sellPct}% ({consensus.sellWeight}w)</span>
            <span style={S.consLbl('#f59e0b')}>WAIT {waitPct}% ({consensus.waitWeight}w)</span>
          </div>
          {consensus.riskWarning && (
            <div style={{ fontSize: '12px', color: '#ef4444', padding: '8px 12px', background: '#ef444411', borderRadius: '6px', border: '1px solid #ef444433' }}>
              ⚠️ Risk warning — one or more agents flagged AVOID. Trade with caution.
            </div>
          )}
        </div>
      )}

      {/* Master Verdict */}
      {masterVerdict && (
        <div style={S.masterCard(masterVerdict.final_verdict)}>
          <div style={S.mBadge}>MASTER BRAIN VERDICT</div>
          <div style={S.mVerdict(masterVerdict.final_verdict)}>{masterVerdict.final_verdict}</div>
          <div style={S.mConf}>
            Consensus: {consensus?.consensusStrength ?? '—'}% ({consensus?.agreeingCount ?? 0}/{consensus?.agentCount ?? 0} agents) · Grade: {masterVerdict.grade}
            {masterVerdict.risk_reward && ` · R:R ${masterVerdict.risk_reward}`}
          </div>
          <div style={S.mReason}>{masterVerdict.master_reasoning}</div>
          {masterVerdict.dissenting_agents && (
            <div style={{ fontSize: '13px', color: '#f59e0b', marginBottom: '12px' }}>
              ⚠️ {masterVerdict.dissenting_agents}
            </div>
          )}
          <div style={S.mAction}>
            🎯 {masterVerdict.immediate_action}
          </div>

          {/* Trade levels grid */}
          <div style={S.mGrid}>
            {[
              { label: 'ENTRY ZONE',  value: masterVerdict.entry_zone    || '—',          color: THEME.colors.blue },
              { label: 'STOP LOSS',   value: masterVerdict.stop_loss     ? ''+masterVerdict.stop_loss : '—',    color: '#ef4444' },
              { label: 'TP 1',        value: masterVerdict.tp1           ? ''+masterVerdict.tp1       : '—',    color: '#10b981' },
              { label: 'TP 2',        value: masterVerdict.tp2           ? ''+masterVerdict.tp2       : '—',    color: '#10b981' },
              { label: 'LOT SIZE',    value: masterVerdict.lot_size      ? masterVerdict.lot_size + ' lots' : '—', color: '#fff' },
              { label: 'R:R RATIO',   value: masterVerdict.risk_reward   || '—',          color: '#fff' },
            ].map(item => (
              <div key={item.label} style={S.mStat}>
                <div style={S.mStatLbl}>{item.label}</div>
                <div style={S.mStatVal(item.color)}>{item.value}</div>
              </div>
            ))}
          </div>

          {masterVerdict.key_risks?.length > 0 && (
            <div style={{ marginTop: '16px', fontSize: '13px', color: '#9ca3af' }}>
              <b style={{ color: '#f59e0b' }}>Key Risks: </b>
              {masterVerdict.key_risks.join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* Agent breakdown table */}
      {consensus?.agentBreakdown?.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>
            AGENT VOTE BREAKDOWN (sorted by weight)
          </div>
          {consensus.agentBreakdown.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', gap: '12px' }}>
              <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {DNA_META[a.dna]?.icon || '🤖'} {a.name}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                WR {a.winRate}% · {a.weight}x
              </div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: verdictColor(a.verdict), width: '50px', textAlign: 'right' }}>
                {a.verdict}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', width: '70px', textAlign: 'right' }}>
                {a.winRate}% WR (real)
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ color: '#ef4444', padding: '20px', background: '#ef444411', borderRadius: '8px', border: '1px solid #ef444433' }}>
          {error}
        </div>
      )}
    </div>
  );
}
